var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useCallback, useContext, useMemo, useReducer } from 'react';
import { getIn, setIn, useFormikContext } from 'formik';
import { deleteIn } from '../../utils/object-utils';
import FormContext from './context';
import { _initialState, formReducer } from './reducer';
export var useForm = function (_a) {
    var validationSchema = _a.validationSchema;
    var _b = useReducer(formReducer, _initialState), state = _b[0], dispatch = _b[1];
    var setCustomErrors = useCallback(function (errors) {
        dispatch({ type: 'SET_CUSTOM_ERRORS', payload: errors });
    }, []);
    var setCustomWarnings = useCallback(function (warnings) {
        dispatch({ type: 'SET_CUSTOM_WARNINGS', payload: warnings });
    }, []);
    var setCustomFieldError = useCallback(function (field, error) {
        dispatch({ type: 'SET_CUSTOM_FIELD_ERROR', payload: { field: field, value: error } });
    }, []);
    var setCustomFieldWarning = useCallback(function (field, warning) {
        dispatch({ type: 'SET_CUSTOM_FIELD_WARNING', payload: { field: field, value: warning } });
    }, []);
    return __assign(__assign({}, state), { setErrors: setCustomErrors, setWarnings: setCustomWarnings, setFieldError: setCustomFieldError, setFieldWarning: setCustomFieldWarning, validationSchema: validationSchema });
};
export var useFormContext = function () {
    var customFormContext = useContext(FormContext);
    return customFormContext;
};
export var useFormField = function (name) {
    var _a = useFormikContext(), errors = _a.errors, touched = _a.touched, values = _a.values, setErrors = _a.setErrors;
    var _b = useFormContext(), validationSchema = _b.validationSchema, customErrors = _b.errors, customWarnings = _b.warnings, setCustomFieldError = _b.setFieldError, setCustomFieldWarning = _b.setFieldWarning;
    var error = useMemo(function () { return getIn(errors, name); }, [errors, name]);
    var customError = useMemo(function () { return getIn(customErrors, name); }, [customErrors, name]);
    var customWarning = useMemo(function () { return getIn(customWarnings, name); }, [customWarnings, name]);
    var value = useMemo(function () { return getIn(values, name); }, [values, name]);
    var _touched = useMemo(function () { return !!getIn(touched, name); }, [touched, name]);
    /**
     * Function to set a custom error for a field.
     * WARNING: Use with caution. Once set, it is the caller's responsibility to unset it.
     * Call it without any arguments like: `setCustomError()` to remove the error for that field.
     */
    var setCustomError = useCallback(function (error) { return setCustomFieldError(name, error); }, [setCustomFieldError, name]);
    /**
     * Function to set a custom warning for a field.
     * WARNING: Use with caution. Once set, it is the caller's responsibility to unset it.
     * Call it without any arguments like: `setCustomWarning()` to remove the warning for that field.
     */
    var setCustomWarning = useCallback(function (warning) { return setCustomFieldWarning(name, warning); }, [setCustomFieldWarning, name]);
    /**
     * Function to set an error for a field.
     * Call it without any arguments like: `setError()` to remove the error for that field.
     *
     * The error set by this function can be overwritten by Formik whenever validation runs.
     * It is recommended to use `setCustomError` for errors that you need to persist across validation re-runs.
     */
    var setError = useCallback(function (newError) {
        if (newError !== error) {
            if (newError) {
                setErrors(setIn(errors, name, newError));
            }
            else {
                setErrors(deleteIn(errors, name));
            }
        }
    }, [error, setErrors, errors, name]);
    /**
     * Validate field with a custom value, instead of the actual field value.
     * Useful in situations where you need to run validation and set errors **before** that value has been passed to
     * Formik.
     *
     * Falls back to using the already set value when no function argument is passed.
     * **Note that** this does not mean passing `undefined` or `null` or any other `falsy` argument in the function
     * parameter. You need to explicitly pass no argument like `validateField()` to fall back to using already set
     * value.
     *
     * This sets the error in the Formik `errors` state rather than custom `errors` state.
     *
     * TODO: Take into account other validation techniques like form-level validation function or field-level
     * validation function, instead of just validation schema.
     */
    var validateField = useCallback(function (newValue, shouldSetError) {
        if (shouldSetError === void 0) { shouldSetError = true; }
        if (arguments.length === 0) {
            newValue = value;
        }
        // Hack for now to always return a promise.
        if (validationSchema) {
            return (validationSchema
                // TODO: Apply the perf improvement for `values` by using `ref`.
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                .validateAt(name, setIn(values, name, newValue))
                .then(function () {
                if (shouldSetError) {
                    setError();
                }
            })
                .catch(function (error) {
                if (shouldSetError) {
                    setError(error.message);
                }
                return error.message;
            }));
        }
        return new Promise(function (resolve) { return resolve(); });
    }, [validationSchema, name, values, setError, value]);
    return {
        error: error,
        customError: customError,
        customWarning: customWarning,
        touched: _touched,
        setCustomError: setCustomError,
        setCustomWarning: setCustomWarning,
        setError: setError,
        validateField: validateField,
        value: value,
    };
};
